import { PageResult } from './types'

// Batch search doesn't make sense with pagination/embeds so this type function strips the
// irrelevant fields to avoid having to repeat type definition of the filters
export type BatchSearch<FiltersT> = Omit<FiltersT, 'page' | 'pageSize' | 'embeds'>

export type Batch = { batchId: string }

export type BatchItem<ItemT, ParamsT = unknown, ErrorsT = unknown> = {
  itemId: string
  item: ItemT
  params: ParamsT
  errors: ErrorsT
}

export type BatchItemAction<ParamsT> = { itemId: string; params: ParamsT }

export type BatchResult = { success: number; failure: number }

export type BatchRoutes<FiltersT, ItemT, ParamsT, ErrorsT, BodyT = object> = {
  init: (filters: FiltersT, body: object) => Promise<Batch>
  preview: (
    batchId: string,
    pagination: { page: number; pageSize: number },
  ) => Promise<PageResult<BatchItem<ItemT, ParamsT, ErrorsT>>>
  update: (batchId: string, actions: BatchItemAction<ParamsT>[]) => Promise<true>
  execute: (batchId: string, body?: BodyT) => Promise<BatchResult>
}

export enum BatchStepEnum {
  INITIATE = 'init',
  PREVIEW = 'preview',
  UPDATE = 'update',
  EXECUTE = 'execute',
}

export type BatchedAction<TParams = unknown, TBody = unknown, TResult = unknown> = {
  params: TParams
  body: TBody
  result: TResult
}

export type InitTableBatchAction = BatchedAction<
  Record<string, unknown>,
  Record<string, unknown>,
  { batchId: string }
>

export type PreviewBatchedAction<TBody = unknown, TResult = unknown> = Omit<
  BatchedAction<unknown, TBody, TResult>,
  'params'
>

export type ExecuteBatchedAction<TBody = unknown> = Omit<
  BatchedAction<unknown, TBody, BatchResult>,
  'params'
>

export type BatchActionConfig = {
  path?: string
  headers?: Record<string, string>
}

export type BatchConfig = {
  baseUrl: string
  [BatchStepEnum.INITIATE]?: BatchActionConfig
  [BatchStepEnum.PREVIEW]?: BatchActionConfig
  [BatchStepEnum.UPDATE]?: BatchActionConfig
  [BatchStepEnum.EXECUTE]?: BatchActionConfig
}

export type BaseBatchResult = {
  batchId: string
  total: number
}

export type BatchRouteActions = {
  init: BatchedAction
  update: BatchedAction
  preview: PreviewBatchedAction
  execute: ExecuteBatchedAction
}

export type BatchActionsConfig<BatchRouteOverrides extends Partial<BatchRouteActions>> = Omit<
  BatchRouteActions,
  // Based on the overrides provided, omit them from the default BatchRouteActions type.
  // We do this since we want our overrides to take precedence
  keyof BatchRouteOverrides
> &
  // Ensure the overrides are used instead of the default.
  // This ensures the config will always have some defined type structure.
  BatchRouteOverrides

export type BatchingRoutes<Actions extends Partial<BatchRouteActions>> = {
  init: (
    body?: BatchActionsConfig<Actions>['init']['body'],
    params?: BatchActionsConfig<Actions>['init']['params'],
  ) => Promise<BatchActionsConfig<Actions>['init']['result']>
  update: (
    batchId: string,
    body?: BatchActionsConfig<Actions>['update']['body'],
    params?: BatchActionsConfig<Actions>['update']['params'],
  ) => Promise<BatchActionsConfig<Actions>['update']['result']>
  preview: (
    batchId: string,
    body?: BatchActionsConfig<Actions>['preview']['body'],
  ) => Promise<BatchActionsConfig<Actions>['preview']['result']>
  execute: (
    batchId: string,
    body?: BatchActionsConfig<Actions>['execute']['body'],
  ) => Promise<BatchActionsConfig<Actions>['execute']['result']>
}
